import { Component } from '@theme/component';

/**
 * A tabbed content section for community programs.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} nav - The tab navigation container.
 * @property {HTMLElement[]} tabs - The tab button elements.
 * @property {HTMLElement[]} panels - The tab panel elements.
 *
 * @extends {Component<Refs>}
 */
class CommunityTabs extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.nav?.addEventListener('keydown', (e) => {
      const tabs = this.refs.tabs;
      const currentIndex = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      let nextIndex;

      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      const nextTab = tabs[nextIndex];
      if (!nextTab) return;
      this.#activateTab(nextTab);
      nextTab.focus();
      nextTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  /** @param {Event} event */
  selectTab(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const clickedTab = /** @type {HTMLElement | null} */ (target.closest('[ref="tabs[]"]'));
    if (!clickedTab) return;
    this.#activateTab(clickedTab);
  }

  /** @param {HTMLElement} tab */
  #activateTab(tab) {
    const index = tab.dataset.index;

    for (const t of this.refs.tabs) {
      t.classList.remove('community-tabs__tab--active');
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    }

    for (const panel of this.refs.panels) {
      panel.classList.remove('community-tabs__panel--active');
    }

    tab.classList.add('community-tabs__tab--active');
    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');

    const activePanel = this.refs.panels.find((p) => p.dataset.index === index);
    if (activePanel) {
      activePanel.classList.add('community-tabs__panel--active');
    }
  }
}

customElements.define('community-tabs', CommunityTabs);
